import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { useCreateProject, useUpdateProject } from '../hooks/queries';
import { ModalPortal } from './ModalPortal';
import { useWorkspace } from '../workspace/WorkspaceContext';

export const PROJECT_PALETTE = [
  '#c2491d',
  '#cf8a08',
  '#6b8e23',
  '#2e7d5b',
  '#1f7a8c',
  '#2f6bed',
  '#6d4fc2',
  '#b13d80',
  '#8d5a3b',
  '#5c6670'
];

interface ProjectDialogProps {
  onClose: () => void;
  /** Podany = edycja istniejącego projektu; brak = tworzenie nowego. */
  project?: { id: string; name: string; color: string };
}

export function ProjectDialog({ onClose, project }: ProjectDialogProps) {
  const { t } = useTranslation();
  const { workspace } = useWorkspace();
  const createProject = useCreateProject(workspace.id);
  const updateProject = useUpdateProject(workspace.id);
  const editing = project != null;
  const [name, setName] = useState(project?.name ?? '');
  // Kolor spoza palety (np. z importu) zostawiamy jako wybrany, żeby zapis
  // przypadkiem go nie podmienił.
  const [color, setColor] = useState(project?.color ?? PROJECT_PALETTE[0]);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const pending = createProject.isPending || updateProject.isPending;

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || pending) return;
    if (editing) {
      updateProject.mutate(
        { projectId: project.id, patch: { name: trimmed, color } },
        { onSuccess: onClose }
      );
    } else {
      createProject.mutate({ name: trimmed, color }, { onSuccess: onClose });
    }
  };

  return (
    <ModalPortal>
    <div className="modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <form className="modal" onSubmit={submit}>
        <h2>{editing ? t('project.edit') : t('project.new')}</h2>
        <div className="modal-field">
          <span className="kicker">{t('project.name')}</span>
          <input
            ref={inputRef}
            className="field-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('project.namePlaceholder')}
          />
        </div>
        <div className="modal-field">
          <span className="kicker">{t('project.color')}</span>
          <div className="palette">
            {(PROJECT_PALETTE.includes(color)
              ? PROJECT_PALETTE
              : [color, ...PROJECT_PALETTE]
            ).map((swatch) => (
              <button
                key={swatch}
                type="button"
                className={`palette-swatch${swatch === color ? ' on' : ''}`}
                style={{ background: swatch }}
                onClick={() => setColor(swatch)}
                aria-label={swatch}
              />
            ))}
          </div>
        </div>
        {!editing && <div className="modal-hint">{t('project.ownHint')}</div>}
        <div className="modal-actions">
          <button type="button" className="btn btn-ghost" onClick={onClose}>
            {t('common.cancel')}
          </button>
          <button type="submit" className="btn btn-primary" disabled={!name.trim() || pending}>
            {editing ? t('common.save') : t('common.create')}
          </button>
        </div>
      </form>
    </div>
    </ModalPortal>
  );
}
